<template>
  <div class="uv">
    <div v-if="endedAgo" class="uv-status-row">
      <span class="uv-meta-right">{{ endedAgo }}</span>
    </div>

    <h1 class="uv-title">{{ castTitle }}</h1>
    <p class="uv-meta">
      {{ durationLabel }} · {{ participantCount }} participants
      <template v-if="hostName"> · {{ hostName }} (host)</template>
    </p>

    <div v-if="recordingBlocked" class="uv-rec-blocked">
      This session was not recorded: the account was out of storage when it
      started. The session itself ran normally.
      <router-link to="/dashboard/account">Plan &amp; usage →</router-link>
    </div>

    <div v-else-if="recordingPending" class="uv-rec-pending">
      <span class="uv-rec-pending-spin"></span>
      Recording is processing - it will appear here and under /recordings when
      ready.
    </div>

    <div v-if="showDeliverables" class="uv-deliverables">
      <div class="uv-deliv">
        <div class="uv-deliv-icon" style="color: #d3ca57">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        </div>
        <div class="uv-deliv-body">
          <div class="uv-deliv-title">Full transcript</div>
          <div class="uv-deliv-meta">
            {{ transcriptWordCount }} words · {{ transcriptSpeakers }} speakers
          </div>
        </div>
        <span
          class="uv-deliv-status"
          :class="transcriptReady ? 'ok' : 'pending'"
        >
          <svg
            v-if="transcriptReady"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <svg
            v-else
            class="uv-spin"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-6.2-8.55" />
          </svg>
        </span>
      </div>

      <div class="uv-deliv">
        <div class="uv-deliv-icon" style="color: #d3ca57">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
        </div>
        <div class="uv-deliv-body">
          <div class="uv-deliv-title">Recording (1080p)</div>
          <div class="uv-deliv-meta">
            <template v-if="recordingReady">Ready to play</template>
            <template v-else>Encoding stems · ETA ~4m</template>
          </div>
        </div>
        <span
          class="uv-deliv-status"
          :class="recordingReady ? 'ok' : 'pending'"
        >
          <svg
            v-if="recordingReady"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <svg
            v-else
            class="uv-spin"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-6.2-8.55" />
          </svg>
        </span>
      </div>

      <div class="uv-deliv">
        <div class="uv-deliv-icon" style="color: #d3ca57">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 3v4" />
            <path d="M19 17v4" />
            <path d="M3 5h4" />
            <path d="M17 19h4" />
            <path d="M14 4l-3 3 3 3-3 3 3 3-3 3" />
            <path d="M9 4l3 3-3 3 3 3-3 3 3 3" />
          </svg>
        </div>
        <div class="uv-deliv-body">
          <div class="uv-deliv-title">Insights</div>
          <div class="uv-deliv-meta">{{ insightsCount }} extracted</div>
        </div>
        <span class="uv-deliv-status" :class="insightsReady ? 'ok' : 'pending'">
          <svg
            v-if="insightsReady"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <svg
            v-else
            class="uv-spin"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 12a9 9 0 1 1-6.2-8.55" />
          </svg>
        </span>
      </div>
    </div>

    <form
      v-if="showDeliverables && !recordingReady"
      class="uv-notify"
      @submit.prevent="submitNotify"
    >
      <span class="uv-notify-icon">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </span>
      <span class="uv-notify-text">
        Recording still encoding. We can email it to you when ready.
      </span>
      <input
        v-model="notifyEmail"
        class="uv-notify-input"
        type="email"
        placeholder="you@newsroom.com"
        required
      />
      <button class="uv-notify-btn" type="submit">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
          />
          <polyline points="22,6 12,13 2,6" />
        </svg>
        Notify me
      </button>
    </form>

    <nav class="uv-tabs">
      <button
        v-for="t in visibleTabs"
        :key="t.key"
        :class="['uv-tab', { active: activeTab === t.key }]"
        @click="$emit('tab', t.key)"
        type="button"
      >
        <span class="uv-tab-icon" v-html="t.icon"></span>
        {{ t.label }}
      </button>
    </nav>

    <UsersTab
      v-if="activeTab === 'Users'"
      :users="sortedUsers"
      :ended-on="endedOn"
    />
    <PollsTab v-else-if="activeTab === 'Polls'" :polls="pollList" />
    <PresentationTab
      v-else-if="activeTab === 'Presentation'"
      :presentations="presentationList"
    />
    <FeedbackTab
      v-else-if="activeTab === 'Feedback'"
      :initial-rating="rating"
      :already-submitted="feedbackSubmitted"
      :existing="feedbackExisting"
      @submit="onFeedbackSubmit"
    />
    <RecordingTab
      v-else-if="activeTab === 'Recording' && recording"
      :recording="recording"
      :public-meeting-id="publicMeetingId"
    />
    <div v-else class="uv-coming">
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p>{{ activeTab }} view is processing. Check back shortly.</p>
    </div>
  </div>
</template>

<script>
import UsersTab from './UsersTab.vue';
import PollsTab from './PollsTab.vue';
import PresentationTab from './PresentationTab.vue';
import FeedbackTab from './FeedbackTab.vue';
import RecordingTab from './RecordingTab.vue';

const TABS = [
  {
    key: 'Transcription',
    label: 'Transcription',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
  },
  {
    key: 'Insights',
    label: 'Insights',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4l-3 3 3 3-3 3 3 3-3 3"/><path d="M9 4l3 3-3 3 3 3-3 3 3 3"/></svg>',
  },
  {
    key: 'Shorts',
    label: 'Shorts Studio',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
  },
  {
    key: 'Users',
    label: 'Users',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
  },
  {
    key: 'Polls',
    label: 'Polls',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>',
  },
  {
    key: 'Presentation',
    label: 'Presentation',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
  },
  {
    key: 'Feedback',
    label: 'Feedback',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  },
  {
    key: 'Recording',
    label: 'Recording',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>',
  },
];

export default {
  name: 'UnlockedView',
  components: {
    UsersTab,
    PollsTab,
    PresentationTab,
    FeedbackTab,
    RecordingTab,
  },
  props: {
    activeTab: { type: String, default: 'Users' },
    castTitle: { type: String, default: 'Cast' },
    sessionKind: { type: String, default: 'cast' },
    durationLabel: { type: String, default: '' },
    participantCount: { type: Number, default: 0 },
    hostName: { type: String, default: '' },
    endedAgo: { type: String, default: '' },
    endedOn: { type: Number, default: 0 },
    sortedUsers: { type: Array, default: () => [] },
    pollList: { type: Array, default: () => [] },
    presentationList: { type: Array, default: () => [] },
    transcriptReady: { type: Boolean, default: false },
    recordingReady: { type: Boolean, default: false },
    insightsReady: { type: Boolean, default: false },
    transcriptWordCount: { type: [Number, String], default: '-' },
    transcriptSpeakers: { type: [Number, String], default: '-' },
    insightsCount: { type: [Number, String], default: '-' },
    rating: { type: Number, default: 0 },
    loggedIn: { type: Boolean, default: false },
    feedbackSubmitted: { type: Boolean, default: false },
    feedbackExisting: { type: Object, default: null },
    recording: { type: Object, default: null },
    recordingPending: { type: Boolean, default: false },
    // refused for want of storage: there is no recording coming, so say so
    recordingBlocked: { type: Boolean, default: false },
    publicMeetingId: { type: String, default: null },
  },
  data() {
    return {
      tabs: TABS,
      notifyEmail: '',
      // Hide the deliverables row (transcript / recording / insights
      // tiles) while their backends aren't wired. Flip to true when
      // any of them lands.
      showDeliverables: false,
    };
  },
  computed: {
    readyCount() {
      return [
        this.transcriptReady,
        this.recordingReady,
        this.insightsReady,
      ].filter(Boolean).length;
    },
    visibleTabs() {
      const hidden = new Set(['Transcription', 'Insights', 'Shorts']);
      return this.tabs.filter((t) => {
        if (hidden.has(t.key)) return false;
        if (t.key === 'Feedback' && !this.loggedIn) return false;
        if (t.key === 'Recording' && !this.recording) return false;
        return true;
      });
    },
  },
  methods: {
    submitNotify() {
      if (!this.notifyEmail) return;
      this.$emit('notify', this.notifyEmail);
    },
    onFeedbackSubmit(payload, done) {
      this.$emit('feedback-submit', payload, done);
    },
  },
};
</script>

<style scoped>
.uv {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: #fff;
}
.uv-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.uv-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(211, 202, 87, 0.08);
  border: 1px solid rgba(211, 202, 87, 0.28);
  color: #d3ca57;
}
.uv-pill-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d3ca57;
  box-shadow: 0 0 8px rgba(211, 202, 87, 0.6);
}
.uv-meta-right {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}
.uv-title {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 6px;
  line-height: 1.2;
  overflow-wrap: break-word;
  word-wrap: break-word;
}
.uv-meta {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 22px;
  overflow-wrap: break-word;
}

.uv-deliverables {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
@media (max-width: 900px) {
  .uv-deliverables {
    grid-template-columns: 1fr;
  }
}
.uv-deliv {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 14px 16px;
}
.uv-deliv-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(211, 202, 87, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.uv-deliv-body {
  flex: 1;
  min-width: 0;
}
.uv-deliv-title {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}
.uv-deliv-meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 2px;
}
.uv-deliv-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
}
.uv-deliv-status.ok {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.1);
}
.uv-deliv-status.pending {
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.04);
}
.uv-spin {
  animation: uv-rotate 0.9s linear infinite;
}
@keyframes uv-rotate {
  to {
    transform: rotate(360deg);
  }
}

.uv-notify {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 22px;
  flex-wrap: wrap;
}
.uv-notify-icon {
  color: rgba(255, 255, 255, 0.55);
  flex-shrink: 0;
}
.uv-notify-text {
  flex: 1;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  min-width: 200px;
}
.uv-notify-input {
  flex: 0 1 220px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
  font-family: inherit;
  outline: none;
}
.uv-notify-input:focus {
  border-color: rgba(211, 202, 87, 0.4);
}
.uv-notify-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}
.uv-notify-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.uv-notify-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.22);
}

.uv-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 18px;
  padding-bottom: 8px;
}
.uv-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, color 0.15s ease;
}
.uv-tab:hover {
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.03);
}
.uv-tab.active {
  background: #d3ca57;
  color: #0b0c14;
}
.uv-tab.active .uv-tab-icon {
  color: #0b0c14;
}
.uv-tab-icon {
  display: inline-flex;
  align-items: center;
  color: currentColor;
}

.uv-coming {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
}
.uv-coming p {
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}
@media (max-width: 540px) {
  .uv-title {
    font-size: 22px;
  }
  .uv-deliv {
    padding: 12px 14px;
  }
  .uv-deliv-title {
    font-size: 12px;
  }
  .uv-deliv-meta {
    font-size: 10px;
  }
  .uv-notify {
    padding: 12px;
  }
  .uv-notify-input {
    flex: 1 1 100%;
  }
  .uv-notify-btn {
    flex: 1 1 100%;
    justify-content: center;
  }
  .uv-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 10px;
    margin: 0 -4px 14px;
    padding-left: 4px;
    padding-right: 4px;
  }
  .uv-tabs::-webkit-scrollbar {
    height: 3px;
  }
  .uv-tabs::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
  .uv-tab {
    flex-shrink: 0;
    font-size: 12px;
    padding: 6px 12px;
  }
  .uv-pill {
    font-size: 11px;
    padding: 5px 10px;
  }
}
.uv-rec-blocked {
  border: 1px solid rgba(240, 196, 90, 0.35);
  background: rgba(240, 196, 90, 0.08);
  color: #f4e2b0;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 14px;
}
.uv-rec-blocked a {
  color: #d7df23;
  margin-left: 6px;
  white-space: nowrap;
}
.uv-rec-pending {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 0 4px;
  padding: 10px 14px;
  border: 1px solid rgba(211, 202, 87, 0.35);
  border-radius: 8px;
  background: rgba(211, 202, 87, 0.07);
  color: #d3ca57;
  font-size: 12.5px;
}
.uv-rec-pending-spin {
  width: 12px;
  height: 12px;
  flex: 0 0 auto;
  border-radius: 50%;
  border: 2px solid rgba(211, 202, 87, 0.3);
  border-top-color: #d3ca57;
  animation: uv-rec-spin 0.9s linear infinite;
}
@keyframes uv-rec-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
